import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LOCALES, OG_LOCALES, localizedPath, translateText } from "@shared/i18n";

export function useDocumentMeta(title: string, description: string, noindex = false) {
  const { locale } = useLanguage();
  useEffect(() => {
    const localTitle = translateText(title, locale);
    const localDescription = translateText(description, locale);
    document.title = localTitle;
    document.documentElement.lang = locale;
    const upsert = (selector: string, attributes: Record<string, string>) => {
      let element = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
      if (!element) {
        element = document.createElement(attributes.rel ? "link" : "meta");
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
    };
    upsert('meta[name="description"]', { name: "description", content: localDescription });
    upsert('meta[property="og:title"]', { property: "og:title", content: localTitle });
    upsert('meta[property="og:description"]', { property: "og:description", content: localDescription });
    upsert('meta[property="og:locale"]', { property: "og:locale", content: OG_LOCALES[locale] });
    upsert('meta[name="robots"]', { name: "robots", content: noindex ? "noindex,nofollow" : "index,follow" });
    const path = window.location.pathname;
    upsert('link[rel="canonical"]', { rel: "canonical", href: window.location.origin + localizedPath(path, locale) });
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(element => element.remove());
    if (!noindex) {
      for (const lang of LOCALES) upsert(`link[rel="alternate"][hreflang="${lang}"]`, { rel: "alternate", hreflang: lang, href: window.location.origin + localizedPath(path, lang) });
      upsert('link[rel="alternate"][hreflang="x-default"]', { rel: "alternate", hreflang: "x-default", href: window.location.origin + localizedPath(path, "en") });
    }
  }, [title, description, noindex, locale]);
}
